import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Clock, FileSpreadsheet, User, Hash, CalendarRange } from 'lucide-react';
import { toast } from 'sonner';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read-only worked-hours report over final Clock-In History records.
// Shared by the admin employee profile tab and the Payroll dialog.
export const WorkHoursHistory = ({ employeeId }) => {
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const generate = async () => {
    if (!fromDate || !toDate) { toast.error('Select both From and To dates'); return; }
    if (toDate < fromDate) { toast.error('To date must be on or after From date'); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/employees/${employeeId}/work-hours-report`, {
        params: { from_date: fromDate, to_date: toDate },
      });
      setReport(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = 'SAN Security Ltd';
      const ws = wb.addWorksheet('Worked Hours Report');
      ws.columns = [
        { width: 14 }, { width: 34 }, { width: 12 }, { width: 14 }, { width: 13 }, { width: 11 }, { width: 18 },
      ];

      ws.mergeCells('A1:G1');
      ws.getCell('A1').value = 'SAN Security Ltd';
      ws.getCell('A1').font = { size: 16, bold: true };
      ws.getCell('A1').alignment = { horizontal: 'center' };
      ws.mergeCells('A2:G2');
      ws.getCell('A2').value = 'Employee Worked Hours Report';
      ws.getCell('A2').font = { size: 12, bold: true, color: { argb: 'FF525252' } };
      ws.getCell('A2').alignment = { horizontal: 'center' };
      ws.addRow([]);

      const meta = [
        ['Employee Name:', report.employee_name],
        ['Employee Code:', report.employee_code || '—'],
        ['Period From:', report.from_date.split('-').reverse().join('/')],
        ['Period To:', report.to_date.split('-').reverse().join('/')],
      ];
      meta.forEach(([k, v]) => {
        const r = ws.addRow([k, v]);
        r.getCell(1).font = { bold: true };
      });
      ws.addRow([]);

      const header = ws.addRow(['Date', 'Event / Site', 'Clock In', 'Clock Out', 'Gross Hours', 'Break', 'Net Worked Hours']);
      header.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      header.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF18181B' } };
      header.alignment = { horizontal: 'center' };

      report.rows.forEach((row) => {
        const missing = row.status && row.status !== 'complete';
        const r = ws.addRow(missing ? [
          row.date, row.site || '—', row.clock_in, row.clock_out, 'NOT COUNTED', '—', 'NOT COUNTED',
        ] : [
          row.date, row.site || '—', row.clock_in, row.clock_out,
          row.gross_hours.toFixed(2),
          `${row.break_minutes} min${row.break_overridden ? ' *' : ''}`,
          row.net_hours.toFixed(2),
        ]);
        r.getCell(2).alignment = { horizontal: 'center' };
        r.getCell(5).alignment = { horizontal: 'right' };
        r.getCell(6).alignment = { horizontal: 'center' };
        r.getCell(7).alignment = { horizontal: 'right' };
        if (row.break_overridden) r.getCell(6).font = { bold: true, color: { argb: 'FF2563EB' } };
        r.eachCell((cell) => {
          if (missing) {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } };
            cell.font = { bold: true, color: { argb: 'FF991B1B' } };
          }
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFCCCCCC' } },
            left: { style: 'thin', color: { argb: 'FFCCCCCC' } },
            bottom: { style: 'thin', color: { argb: 'FFCCCCCC' } },
            right: { style: 'thin', color: { argb: 'FFCCCCCC' } },
          };
        });
      });

      ws.addRow([]);
      const bh = Math.floor((report.total_break_minutes || 0) / 60);
      const bm = (report.total_break_minutes || 0) % 60;
      const totals = [
        ['TOTAL SHIFTS WORKED:', String(report.total_shifts)],
        ['TOTAL GROSS HOURS:', (report.total_gross_hours || 0).toFixed(2)],
        ['TOTAL BREAK TIME:', `${bh} hours ${bm} minutes`],
        ['TOTAL NET WORKED HOURS:', (report.total_net_hours ?? report.total_hours).toFixed(2)],
      ];
      totals.forEach(([label, value]) => {
        const r = ws.addRow([label, label === 'TOTAL BREAK TIME:' ? 'Unpaid breaks' : '', '', '', '', '', value]);
        r.font = { bold: true };
        if (label === 'TOTAL BREAK TIME:') {
          r.getCell(2).font = { italic: true, bold: false, color: { argb: 'FF6B7280' } };
          r.getCell(2).alignment = { horizontal: 'center' };
        }
        r.getCell(7).alignment = { horizontal: 'right' };
        r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFACC15' } };
        r.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFACC15' } };
      });
      const note = ws.addRow(['Breaks deducted automatically: <4h = 0 min · 4–6h = 20 min · >6–8h = 30 min · >8h = 60 min. * = Primary Admin override. Red rows = missing clock in/out, not counted in totals.']);
      note.font = { italic: true, size: 9, color: { argb: 'FF6B7280' } };

      const buffer = await wb.xlsx.writeBuffer();
      const safeName = (report.employee_name || 'employee').replace(/\s+/g, '_');
      saveAs(new Blob([buffer]), `Worked_Hours_${safeName}_${report.from_date}_${report.to_date}.xlsx`);
      if (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        toast.success('Excel downloaded', {
          description: "Saved to your Downloads folder — open the download notification or your browser's Downloads to view it.",
          duration: 10000,
        });
      } else {
        toast.success('Excel downloaded');
      }
    } catch (e) {
      console.error('Excel error:', e);
      toast.error('Failed to generate Excel');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="work-hours-history">
      <Card className="bg-card border-border rounded-sm">
        <CardHeader className="border-b border-border pb-4">
          <CardTitle className="text-sm font-barlow tracking-wider uppercase flex items-center gap-2">
            <Clock className="w-4 h-4 text-primary" strokeWidth={1.5} /> Work Hours History
          </CardTitle>
          <p className="text-[11px] text-muted-foreground font-normal normal-case tracking-normal">
            Worked hours from final Clock In → Clock Out records only. Read-only.
          </p>
        </CardHeader>
        <CardContent className="p-4 space-y-3 max-w-sm">
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">From Date</Label>
            <Input
              type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)}
              className="bg-background border-border rounded-sm" data-testid="work-hours-from"
            />
          </div>
          <div className="space-y-1">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground">To Date</Label>
            <Input
              type="date" value={toDate} onChange={(e) => setToDate(e.target.value)}
              className="bg-background border-border rounded-sm" data-testid="work-hours-to"
            />
          </div>
          <Button
            onClick={generate} disabled={loading}
            className="w-full bg-primary text-primary-foreground hover:bg-yellow-300 rounded-sm font-barlow tracking-wider"
            data-testid="generate-work-hours-btn"
          >
            {loading ? 'Generating…' : 'GENERATE REPORT'}
          </Button>
        </CardContent>
      </Card>

      {report && (
        <Card className="bg-card border-primary/40 rounded-sm" data-testid="work-hours-summary">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 gap-2 text-sm">
              <SummaryRow icon={User} label="Employee" value={report.employee_name} testId="work-hours-employee-name" />
              <SummaryRow icon={Hash} label="Employee Code" value={report.employee_code || '—'} testId="work-hours-employee-code" mono />
              <SummaryRow icon={CalendarRange} label="Period" value={report.period_label} testId="work-hours-period" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="bg-muted/40 border border-border/60 rounded-sm px-3 py-2 text-center">
                <p className="font-mono text-xl text-primary leading-tight" data-testid="work-hours-total-shifts">{report.total_shifts}</p>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total Shifts Worked</p>
              </div>
              <div className="bg-muted/40 border border-border/60 rounded-sm px-3 py-2 text-center">
                <p className="font-mono text-xl text-primary leading-tight" data-testid="work-hours-total-hours">{(report.total_net_hours ?? report.total_hours).toFixed(2)} h</p>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total Net Worked Hours</p>
              </div>
              <div className="bg-muted/40 border border-border/60 rounded-sm px-3 py-2 text-center">
                <p className="font-mono text-base text-foreground leading-tight" data-testid="work-hours-total-gross">{(report.total_gross_hours || 0).toFixed(2)} h</p>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total Gross Hours</p>
              </div>
              <div className="bg-muted/40 border border-border/60 rounded-sm px-3 py-2 text-center">
                <p className="font-mono text-base text-foreground leading-tight" data-testid="work-hours-total-break">
                  {Math.floor((report.total_break_minutes || 0) / 60)}h {(report.total_break_minutes || 0) % 60}m
                </p>
                <p className="text-[9px] uppercase tracking-wider text-muted-foreground">Total Break Time</p>
              </div>
            </div>
            <Button
              onClick={downloadExcel} disabled={downloading || report.rows.length === 0}
              variant="outline"
              className="w-full border-green-600 text-green-500 hover:bg-green-900/20 rounded-sm font-barlow tracking-wider"
              data-testid="download-work-hours-excel"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {downloading ? 'Preparing…' : 'DOWNLOAD EXCEL'}
            </Button>
            {report.rows.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic text-center">No completed worked shifts in this period.</p>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

function SummaryRow({ icon: Icon, label, value, testId, mono }) {
  return (
    <div className="flex items-center gap-2">
      <Icon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
      <span className="text-[11px] uppercase tracking-wider text-muted-foreground w-32 flex-shrink-0">{label}</span>
      <span className={`text-foreground truncate ${mono ? 'font-mono' : ''}`} data-testid={testId}>{value}</span>
    </div>
  );
}

export default WorkHoursHistory;
